import { BACKEND_URL } from "@/lib/api";

// ---- destination metadata ----
export const DESTINATIONS = [
  { id: "website", name: "Website / URL", icon: "Globe", cat: "General", desc: "Link to any website or landing page", modes: ["static", "dynamic"] },
  { id: "vcard", name: "Employee Contact", icon: "Contact", cat: "Business", desc: "Direct or updateable vCard contact", modes: ["static", "dynamic"], featured: true },
  { id: "email", name: "Email", icon: "Mail", cat: "General", desc: "Pre-fill recipient, subject and body", modes: ["static", "dynamic"] },
  { id: "text", name: "Plain Text", icon: "FileText", cat: "General", desc: "Show static text, codes or notes", modes: ["static", "dynamic"] },
  { id: "pdf", name: "PDF Document", icon: "FileCode", cat: "Media", desc: "Share brochures, menus, reports", modes: ["dynamic"] },
  { id: "menu", name: "Restaurant Menu", icon: "Utensils", cat: "Business", desc: "Premium mobile menu — photos, categories, cart & table QRs. AI import from a PDF or photo.", modes: ["dynamic"], featured: true },
  { id: "wifi", name: "WiFi", icon: "Wifi", cat: "General", desc: "Let guests connect to your WiFi with a single scan", modes: ["static"] },
  { id: "coupon", name: "Coupon", icon: "Tag", cat: "Marketing", desc: "Offer, code, validity & redeem flow", modes: ["dynamic"] },
  { id: "event", name: "Event", icon: "Calendar", cat: "Events", desc: "Date, venue & add-to-calendar", modes: ["dynamic"] },
  { id: "app", name: "App Store Link", icon: "Smartphone", cat: "Marketing", desc: "Route iOS & Android automatically", modes: ["dynamic"] },
  { id: "phone", name: "Phone Call", icon: "Phone", cat: "General", desc: "Trigger the phone dialer", modes: ["static", "dynamic"] },
  { id: "sms", name: "SMS", icon: "MessageSquare", cat: "General", desc: "Pre-filled text message", modes: ["static", "dynamic"] },
  { id: "location", name: "Location", icon: "MapPin", cat: "General", desc: "Open maps directions", modes: ["static", "dynamic"] },
  { id: "images", name: "Image Gallery", icon: "Image", cat: "Media", desc: "Hosted photo gallery", modes: ["dynamic"] },
  { id: "video", name: "Video", icon: "Video", cat: "Media", desc: "MP4 upload or YouTube/Vimeo", modes: ["dynamic"] },
  { id: "business_page", name: "Business Page", icon: "Building2", cat: "Business", desc: "Company profile & contact", modes: ["dynamic"] },
  { id: "social_media", name: "Social Hub", icon: "Share2", cat: "Marketing", desc: "All social links in one page", modes: ["dynamic"] },
  { id: "link_in_bio", name: "Link in Bio", icon: "Link2", cat: "Marketing", desc: "One mobile page with all your links, social icons & locations — perfect for Instagram & Facebook bios", modes: ["dynamic"], featured: true },
  { id: "facebook", name: "Facebook", icon: "Facebook", cat: "Marketing", desc: "Direct Facebook page link", modes: ["static", "dynamic"] },
  { id: "linkedin", name: "LinkedIn", icon: "Linkedin", cat: "Marketing", desc: "Direct LinkedIn profile link", modes: ["static", "dynamic"] },
  { id: "mp3", name: "MP3 Audio", icon: "Music", cat: "Media", desc: "Hosted audio player", modes: ["dynamic"] },
  { id: "google_reviews", name: "Google Reviews", icon: "Star", cat: "Feedback", desc: "Send customers directly to your Google review page", modes: ["dynamic"] },
  { id: "rating", name: "Rating (legacy)", icon: "Star", cat: "Feedback", desc: "Legacy rating", modes: ["dynamic"], hidden: true },
  { id: "feedback", name: "Feedback", icon: "MessageCircle", cat: "Feedback", desc: "Feedback form & review collector", modes: ["dynamic"] },
];

export const destMeta = (id) => DESTINATIONS.find((d) => d.id === id) || DESTINATIONS[0];

// ---- vCard string builder (client, for direct QR) ----
const esc = (v) => (v == null ? "" : String(v).replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n"));
export const SOCIAL_LABELS = { facebook: "Facebook", instagram: "Instagram", threads: "Threads", tiktok: "TikTok", x: "X", youtube: "YouTube", vimeo: "Vimeo", linkedin: "LinkedIn", pinterest: "Pinterest", snapchat: "Snapchat", whatsapp: "WhatsApp", telegram: "Telegram", messenger: "Messenger", discord: "Discord", reddit: "Reddit", twitch: "Twitch", spotify: "Spotify", soundcloud: "SoundCloud", applemusic: "Apple Music", github: "GitHub", behance: "Behance", dribbble: "Dribbble", medium: "Medium", yelp: "Yelp", tripadvisor: "TripAdvisor", google: "Google", email: "Email", phone: "Phone", website: "Website", custom: "Link" };
const waLink = (v) => { v = (v || "").trim(); if (!v) return ""; return v.startsWith("http") ? v : "https://wa.me/" + v.replace(/[^0-9]/g, ""); };
export function buildVCardString(d = {}, includePhoto = false) {
  const fn = [d.prefix, d.first_name, d.last_name].filter(Boolean).join(" ");
  const L = ["BEGIN:VCARD", "VERSION:3.0"];
  L.push(`N:${esc(d.last_name)};${esc(d.first_name)};;${esc(d.prefix)};`);
  L.push(`FN:${esc(fn)}`);
  if (d.company) L.push(`ORG:${esc(d.company)}`);
  if (d.job_title) L.push(`TITLE:${esc(d.job_title)}`);
  if (d.mobile) L.push(`TEL;TYPE=CELL:${esc(d.mobile)}`);
  if (d.office_phone) L.push(`TEL;TYPE=WORK,VOICE:${esc(d.office_phone)}`);
  if (d.email) L.push(`EMAIL;TYPE=INTERNET:${esc(d.email)}`);
  if (d.website) L.push(`URL:${esc(d.website)}`);
  let n = 0;
  const _seen = new Set();
  const labeled = (url, label) => { const k = (url || "").trim(); if (!k || _seen.has(k)) return; _seen.add(k); n += 1; L.push(`item${n}.URL:${esc(url)}`); L.push(`item${n}.X-ABLabel:${esc(label)}`); };
  if (d.instagram) labeled(d.instagram, "Instagram");
  if (d.linkedin) labeled(d.linkedin, "LinkedIn");
  (d.social_links || []).forEach((s) => { if (s.url) labeled(s.platform === "whatsapp" ? waLink(s.url) : s.url, SOCIAL_LABELS[s.platform] || "Link"); });
  if (d.whatsapp) labeled(waLink(d.whatsapp), "WhatsApp");
  if (d.maps) labeled(d.maps, "Location");
  (d.extra_links || []).forEach((l) => { if (l.url) labeled(l.url, l.label || "Link"); });
  (d.custom_fields || []).forEach((c) => { const v = (c.value || "").trim(); if (!v) return; if (v.startsWith("http")) labeled(v, c.label || "Info"); else L.push(`X-${(c.label || "INFO").toUpperCase().replace(/[^A-Z0-9]/g, "-")}:${esc(v)}`); });
  if (d.street || d.city || d.region || d.postcode || d.country)
    L.push(`ADR;TYPE=WORK:;;${esc(d.street)};${esc(d.city)};${esc(d.region)};${esc(d.postcode)};${esc(d.country)}`);
  if (d.notes) L.push(`NOTE:${esc(d.notes)}`);
  if (includePhoto && d.photo && d.photo.startsWith("data:")) {
    try { const [h, b64] = d.photo.split(","); const t = (h.split(";")[0].split("/")[1] || "jpeg").toUpperCase(); L.push(`PHOTO;ENCODING=b;TYPE=${t === "JPG" ? "JPEG" : t}:${b64}`); } catch {}
  }
  L.push("END:VCARD");
  return L.join("\r\n");
}

// ---- WiFi payload (escaped per the WIFI: URI scheme) ----
export function wifiEscape(v) {
  return String(v == null ? "" : v).replace(/([\\;,:"])/g, "\\$1");
}
export function buildWifiPayload(d = {}) {
  const auth = d.security === "nopass" ? "nopass" : (d.security || "WPA");
  const ssid = wifiEscape(d.ssid || "");
  const hidden = d.hidden ? "true" : "false";
  let out = `WIFI:T:${auth};S:${ssid};`;
  if (auth !== "nopass") out += `P:${wifiEscape(d.password || "")};`;
  out += `H:${hidden};;`;
  return out;
}

// ---- encoded QR data string ----
export function buildEncodedData({ type, mode, data = {}, publicId, vcardMode, dynamicUrl, domain }) {
  const dyn = dynamicUrl || (domain ? `https://${domain}/api/r/${publicId}` : `${BACKEND_URL}/api/r/${publicId}`);
  if (type === "wifi") return buildWifiPayload(data);
  if (mode === "dynamic") return dyn;
  // static payloads
  switch (type) {
    case "website": return data.url || "https://example.com";
    case "vcard": return buildVCardString(data, false);
    case "email": {
      const p = [];
      if (data.subject) p.push("subject=" + encodeURIComponent(data.subject));
      if (data.body) p.push("body=" + encodeURIComponent(data.body));
      return `mailto:${data.email || ""}` + (p.length ? "?" + p.join("&") : "");
    }
    case "text": return data.text || "Sample text";
    case "phone": return `tel:${data.phone || ""}`;
    case "sms": return `sms:${data.phone || ""}` + (data.message ? `?&body=${encodeURIComponent(data.message)}` : "");
    case "location": return data.maps_url || `https://www.google.com/maps/search/?api=1&query=${data.lat || ""},${data.lng || ""}`;
    case "facebook": return data.url || "https://facebook.com";
    case "linkedin": return data.url || "https://linkedin.com";
    default: return dyn;
  }
}

// ---- qr-code-styling options ----
export const DEFAULT_DESIGN = {
  fg: "#0B0F17", bg: "#FFFFFF", transparent: false,
  useGradient: false, gradientType: "linear", gradientColor: "#6366F1", gradientRotation: 0,
  dotType: "rounded", cornerSquareType: "extra-rounded", cornerDotType: "dot",
  cornerSquareColor: "#0B0F17", cornerDotColor: "#6366F1", useCustomCorners: false,
  logo: null, logoSize: 0.4, logoMargin: 8, hideBackgroundDots: true,
  ecc: "Q", size: 320, margin: 12,
  frame: "none", frameText: "SCAN ME", frameColor: "#6366F1", frameTextColor: "#FFFFFF", frameBg: "#FFFFFF",
};

export function toStylingOptions(design, dataString) {
  const d = { ...DEFAULT_DESIGN, ...design };
  const dotsOptions = { type: d.dotType, color: d.fg };
  if (d.useGradient) {
    dotsOptions.gradient = {
      type: d.gradientType, rotation: (d.gradientRotation || 0) * Math.PI / 180,
      colorStops: [{ offset: 0, color: d.fg }, { offset: 1, color: d.gradientColor }],
    };
  }
  return {
    width: d.size, height: d.size, type: "svg", data: dataString || " ",
    margin: d.margin,
    qrOptions: { errorCorrectionLevel: d.ecc },
    imageOptions: { crossOrigin: "anonymous", margin: d.logoMargin, imageSize: d.logoSize, hideBackgroundDots: d.hideBackgroundDots },
    dotsOptions,
    backgroundOptions: { color: d.transparent ? "rgba(0,0,0,0)" : d.bg },
    cornersSquareOptions: { type: d.cornerSquareType, color: d.useCustomCorners ? d.cornerSquareColor : d.fg },
    cornersDotOptions: { type: d.cornerDotType, color: d.useCustomCorners ? d.cornerDotColor : d.fg },
    image: d.logo || undefined,
  };
}

// ---- scannability score ----
function hexToRgb(h) { const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h || "#000"); return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [0, 0, 0]; }
function luminance(h) { const [r, g, b] = hexToRgb(h).map((c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }); return 0.2126 * r + 0.7152 * g + 0.0722 * b; }
export function contrastRatio(a, b) { const l1 = luminance(a), l2 = luminance(b); const hi = Math.max(l1, l2), lo = Math.min(l1, l2); return (hi + 0.05) / (lo + 0.05); }

export function scanScore(design, dataLength = 60) {
  const d = { ...DEFAULT_DESIGN, ...design };
  let score = 100; const issues = [];
  const cr = contrastRatio(d.fg, d.transparent ? "#FFFFFF" : d.bg);
  if (cr < 2.5) { score -= 45; issues.push("Very low contrast between dots and background."); }
  else if (cr < 4) { score -= 22; issues.push("Low contrast may reduce scan reliability."); }
  if (d.logoSize > 0.5) { score -= 25; issues.push("Logo covers too much of the code."); }
  else if (d.logoSize > 0.42) { score -= 10; issues.push("Large logo — raise error correction to High."); }
  if (d.ecc === "L") { score -= 12; issues.push("Low error correction; use Q or H with a logo."); }
  if (d.logo && d.ecc !== "H" && d.ecc !== "Q") { score -= 15; issues.push("Add higher error correction when using a logo."); }
  if (d.margin < 8) { score -= 10; issues.push("Quiet zone is too small (use 8+)."); }
  if (d.useCustomCorners) {
    if (contrastRatio(d.cornerSquareColor, d.transparent ? "#FFFFFF" : d.bg) < 2.5) { score -= 18; issues.push("Corner frames blend into the background."); }
    if (contrastRatio(d.cornerDotColor, d.transparent ? "#FFFFFF" : d.bg) < 2.5) { score -= 12; issues.push("Inner corner dots blend into the background."); }
  }
  if (dataLength > 300) { score -= 12; issues.push("Dense data — consider a dynamic QR for shorter content."); }
  if (dataLength > 800) { score -= 15; issues.push("Very dense QR; dots become small and hard to scan."); }
  score = Math.max(5, Math.min(100, score));
  return { score: Math.round(score), issues, contrast: Math.round(cr * 10) / 10 };
}

export function improveDesign(design) {
  return { ...design, ecc: "H", margin: Math.max(design.margin || 0, 12), logoSize: Math.min(design.logoSize ?? 0.4, 0.38),
    transparent: false, bg: design.transparent ? "#FFFFFF" : design.bg,
    fg: contrastRatio(design.fg, design.bg) < 4 ? "#0B0F17" : design.fg,
    bg2: undefined };
}
